/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — auth-signin.ts
   Sign in / sign up / forgot password / sign out / session management.
   ═══════════════════════════════════════════════════════════════════════ */

import { state, SECTIONS } from './state';
import {
  FIREBASE_ENABLED,
  ENCRYPTION_ENABLED,
  trackEvent,
  getFirebaseUid,
  genId,
  blockIfSafeMode,
  getUserKey,
} from './config';
import { el, elInput } from './utils';
import {
  cloudLoadUserData,
  migrateToCloud,
  getProjectList,
  getProjectListCloud,
  saveProjectList,
  saveProject,
  migrateLocalStorageKeys,
} from './data';
import {
  AUTH_USERS_KEY,
  AUTH_SESSION_KEY,
  SESSION_EXPIRY_MS,
  SIGNUPS_DISABLED,
  authMsg,
  showAuthView,
  mfaState,
} from './auth-helpers';
import { runPostLoginGate } from './auth-billing';
import { show2FASignInChallenge } from './auth-2fa';

declare const firebase: any;

// ─── Password / user storage (localStorage fallback) ────────────────────────

export async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password + '-noteflow-auth-pepper-v1');
  const hash = await crypto.subtle.digest('SHA-256', data);
  const arr = new Uint8Array(hash);
  let hex: string = '';
  for (let i = 0; i < arr.length; i++) hex += ('0' + arr[i].toString(16)).slice(-2);
  return hex;
}

export function getUsers(): Record<string, any> {
  try { return JSON.parse(localStorage.getItem(AUTH_USERS_KEY) || '{}') || {}; }
  catch (_e) { return {}; }
}

export function saveUsers(users: Record<string, any>): void {
  localStorage.setItem(AUTH_USERS_KEY, JSON.stringify(users));
}

// ─── Sign-up ────────────────────────────────────────────────────────────────

export async function handleSignUp(): Promise<void> {
  if (SIGNUPS_DISABLED) { authMsg('signup-msg', 'error', 'Registration is currently closed. Please check back soon.'); return; }
  blockIfSafeMode('New account registration', function () { _handleSignUpInner(); });
}

async function _handleSignUpInner(): Promise<void> {
  const name: string = elInput('signup-name')?.value.trim() ?? '';
  const email: string = (elInput('signup-email')?.value.trim() ?? '').toLowerCase();
  const pass: string = elInput('signup-password')?.value ?? '';
  const confirm_pass: string = elInput('signup-confirm')?.value ?? '';

  if (!name) { authMsg('signup-msg', 'error', 'Please enter your name.'); return; }
  if (!email || !email.includes('@')) { authMsg('signup-msg', 'error', 'Please enter a valid email.'); return; }
  if (pass.length < 8) { authMsg('signup-msg', 'error', 'Password must be at least 8 characters.'); return; }
  if (!/[A-Z]/.test(pass) || !/[0-9]/.test(pass)) { authMsg('signup-msg', 'error', 'Password must contain at least one uppercase letter and one number.'); return; }
  if (pass !== confirm_pass) { authMsg('signup-msg', 'error', 'Passwords do not match.'); return; }

  if (FIREBASE_ENABLED) {
    authMsg('signup-msg', 'info', 'Creating account...');
    state.firebaseAuth.createUserWithEmailAndPassword(email, pass)
      .then(function (cred: any) {
        return cred.user.updateProfile({ displayName: name });
      })
      .then(function () {
        if (state.firebaseDb) {
          state.firebaseDb.collection('users').doc(state.firebaseAuth.currentUser.uid).set({
            name: name, email: email, createdAt: new Date().toISOString(), subscriptionStatus: 'trial', trialEndsAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
          }, { merge: true });
        }
        authMsg('signup-msg', 'success', 'Account created! Redirecting...');
        setTimeout(function () { loginSession(email, name); }, 800);
      })
      .catch(function (err: any) {
        const msg: string = err.code === 'auth/email-already-in-use' ? 'An account with this email already exists.'
          : err.code === 'auth/weak-password' ? 'Password is too weak.'
          : err.message;
        authMsg('signup-msg', 'error', msg);
      });
  } else {
    const users = getUsers();
    if (users[email]) { authMsg('signup-msg', 'error', 'An account with this email already exists.'); return; }
    const hashed: string = await hashPassword(pass);
    users[email] = { name: name, password: hashed };
    saveUsers(users);
    authMsg('signup-msg', 'success', 'Account created! Redirecting...');
    setTimeout(function () { loginSession(email, name); }, 800);
  }
}

// ─── Sign-in ────────────────────────────────────────────────────────────────

export async function handleSignIn(): Promise<void> {
  const email: string = (elInput('signin-email')?.value.trim() ?? '').toLowerCase();
  const pass: string = elInput('signin-password')?.value ?? '';

  if (!email) { authMsg('signin-msg', 'error', 'Please enter your email.'); return; }
  if (!pass) { authMsg('signin-msg', 'error', 'Please enter your password.'); return; }

  if (FIREBASE_ENABLED) {
    authMsg('signin-msg', 'info', 'Signing in...');
    state.firebaseAuth.signInWithEmailAndPassword(email, pass)
      .then(function (cred: any) {
        const name: string = cred.user.displayName || email;
        loginSession(email, name);
      })
      .catch(function (err: any) {
        if (err.code === 'auth/multi-factor-auth-required') {
          mfaState.mfaResolver = err.resolver;
          show2FASignInChallenge();
          return;
        }
        const msg: string = err.code === 'auth/user-not-found' ? 'No account found with this email.'
          : err.code === 'auth/wrong-password' ? 'Invalid email or password.'
          : err.code === 'auth/invalid-credential' ? 'Invalid email or password.'
          : err.message;
        authMsg('signin-msg', 'error', msg);
      });
  } else {
    const users = getUsers();
    const user = users[email];
    const hashed: string = await hashPassword(pass);
    if (!user || user.password !== hashed) {
      authMsg('signin-msg', 'error', 'Invalid email or password.');
      return;
    }
    loginSession(email, user.name);
  }
}

// ─── Forgot password ────────────────────────────────────────────────────────

export async function handleForgotPassword(): Promise<void> {
  const email: string = (elInput('forgot-email')?.value.trim() ?? '').toLowerCase();

  if (FIREBASE_ENABLED) {
    if (!email || !email.includes('@')) { authMsg('forgot-msg', 'error', 'Please enter a valid email.'); return; }
    authMsg('forgot-msg', 'info', 'Sending reset email...');
    state.firebaseAuth.sendPasswordResetEmail(email)
      .then(function () {
        authMsg('forgot-msg', 'success', 'Password reset email sent! Check your inbox.');
        setTimeout(function () { showAuthView('signin'); }, 2000);
      })
      .catch(function (err: any) {
        const msg: string = err.code === 'auth/user-not-found' ? 'No account found with this email.' : err.message;
        authMsg('forgot-msg', 'error', msg);
      });
  } else {
    const pass: string = elInput('forgot-password')?.value ?? '';
    const confirm_pass: string = elInput('forgot-confirm')?.value ?? '';
    if (!email || !email.includes('@')) { authMsg('forgot-msg', 'error', 'Please enter a valid email.'); return; }
    const users = getUsers();
    if (!users[email]) { authMsg('forgot-msg', 'error', 'No account found with this email.'); return; }
    if (pass.length < 8) { authMsg('forgot-msg', 'error', 'New password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(pass) || !/[0-9]/.test(pass)) { authMsg('forgot-msg', 'error', 'Password must contain at least one uppercase letter and one number.'); return; }
    if (pass !== confirm_pass) { authMsg('forgot-msg', 'error', 'Passwords do not match.'); return; }
    users[email].password = await hashPassword(pass);
    saveUsers(users);
    authMsg('forgot-msg', 'success', 'Password updated! Redirecting to sign in...');
    setTimeout(function () { showAuthView('signin'); }, 1200);
  }
}

// ─── Session lifecycle ──────────────────────────────────────────────────────

/**
 * Establishes a user session after authentication, migrates legacy data, and loads the project list.
 */
export function loginSession(email: string, name: string): void {
  trackEvent('logins');
  localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify({ email: email, name: name, loginAt: Date.now() }));
  state.currentUserEmail = email;
  state.currentUserName = name;

  migrateLocalStorageKeys();

  // Phase 3 RBAC: auto-accept any pending firm invitation for this email
  // Best-effort, non-blocking — silent on failure
  try {
    const fb = (window as any).firebase;
    if (FIREBASE_ENABLED && fb && fb.functions && fb.auth().currentUser) {
      fb.functions().httpsCallable('acceptInvitation')()
        .then(function(res: any) {
          if (res?.data?.activated) {
            console.log('[NoteFlow] Firm invitation accepted, role:', res.data.role);
          }
        })
        .catch(function() { /* silent */ });
    }
  } catch (_e) { /* silent */ }

  // Migration: check for old fsgen-state key
  const oldState = localStorage.getItem('fsgen-state');
  if (oldState) {
    try {
      const parsed = JSON.parse(oldState);
      const id: string = genId();
      const proj: any = {
        currentData: {} as Record<string, any>,
        priorData: {} as Record<string, any>,
        company: parsed.company || '',
        period: parsed.period || '',
        priorPeriod: '',
        currency: parsed.currency || '$',
        notes: {},
      };
      SECTIONS.forEach(function (s: string) {
        proj.currentData[s] = (parsed.data && parsed.data[s]) ? parsed.data[s] : [];
        proj.priorData[s] = [];
      });
      localStorage.setItem('fsgen-project-' + id, JSON.stringify(proj));
      const mList: any[] = getProjectList(email);
      mList.push({ id: id, name: parsed.company || 'Migrated Project', updatedAt: new Date().toISOString() });
      saveProjectList(email, mList);
      localStorage.removeItem('fsgen-state');
    } catch (_e) {
      localStorage.removeItem('fsgen-state');
    }
  }

  const badge = document.getElementById('security-badge');
  if (badge && ENCRYPTION_ENABLED) badge.style.display = 'inline';
  const cloudBadge = document.getElementById('cloud-sync-badge');
  if (cloudBadge && getFirebaseUid()) cloudBadge.style.display = 'inline';

  const migrationDone: Promise<void> = getFirebaseUid() ? migrateToCloud() : Promise.resolve();
  migrationDone.then(function () {
    if (getFirebaseUid()) {
      Promise.all([
        cloudLoadUserData('firmProfile'),
        cloudLoadUserData('template'),
        cloudLoadUserData('patterns'),
        cloudLoadUserData('dismissed'),
      ]).then(function (results: any[]) {
        const uk = getUserKey();
        if (results[0]) localStorage.setItem('noteflow-firmprofile-' + uk, JSON.stringify(results[0]));
        if (results[1]) localStorage.setItem('noteflow-template-' + uk, JSON.stringify(results[1]));
        if (results[2]) localStorage.setItem('noteflow-patterns-' + uk, JSON.stringify(results[2]));
        if (results[3]) localStorage.setItem('noteflow-dismissed-' + uk, JSON.stringify(results[3]));
      });

      getProjectListCloud(function (mergedList: any[]) {
        saveProjectList(email, mergedList);
      });
    }

    runPostLoginGate();
  });
}

export function handleSignOut(): void {
  if (state.currentProjectId) saveProject();
  if (FIREBASE_ENABLED && state.firebaseAuth) {
    state.firebaseAuth.signOut();
  }
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('fsgen-') || key === AUTH_SESSION_KEY || key.startsWith('noteflow-') || key.startsWith('firm-'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(function (k: string) { localStorage.removeItem(k); });
  state.currentUserEmail = null;
  state.currentUserName = null;
  state.currentProjectId = null;
  state._cachedEncKey = null;
  state._cachedEncKeyEmail = null;
  state._cachedTemplate = null;
  el('app-wrapper')?.classList.remove('visible');
  el('dashboard')?.classList.remove('visible');
  const subGate = el('subscription-gate');
  if (subGate) subGate.style.display = 'none';
  const loginScreen = el('login-screen');
  if (loginScreen) loginScreen.style.display = 'flex';
  const badge = document.getElementById('security-badge');
  if (badge) badge.style.display = 'none';
  const cloudBadge = document.getElementById('cloud-sync-badge');
  if (cloudBadge) cloudBadge.style.display = 'none';
  showAuthView('signin');
}

export function isSessionExpired(): boolean {
  try {
    const session = JSON.parse(localStorage.getItem(AUTH_SESSION_KEY) || 'null');
    if (!session || !session.loginAt) return true;
    return (Date.now() - session.loginAt) > SESSION_EXPIRY_MS;
  } catch (_e) { return true; }
}

// ─── Auth Form Enter Key Handlers ──────────────────────────────────────────

export function initAuthListeners(): void {
  document.querySelectorAll('#auth-signin input').forEach(function (node: Element) {
    node.addEventListener('keydown', function (e: Event) {
      if ((e as KeyboardEvent).key === 'Enter') handleSignIn();
    });
  });
  document.querySelectorAll('#auth-signup input').forEach(function (node: Element) {
    node.addEventListener('keydown', function (e: Event) {
      if ((e as KeyboardEvent).key === 'Enter') handleSignUp();
    });
  });
  document.querySelectorAll('#auth-forgot input').forEach(function (node: Element) {
    node.addEventListener('keydown', function (e: Event) {
      if ((e as KeyboardEvent).key === 'Enter') handleForgotPassword();
    });
  });
}
